import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Brain, Menu, X, Phone } from "lucide-react";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHome = location.pathname === "/";

  const navLinks = [
    { label: "Industries", href: isHome ? "#industries" : "/#industries" },
    { label: "Products", href: isHome ? "#products" : "/#products" },
    { label: "Solutions", href: isHome ? "#solutions" : "/#solutions" },
    { label: "Contact", href: isHome ? "#contact" : "/#contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "navbar-solid" : "navbar-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 lg:h-18">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-lg bg-[#00E599]/10 border border-[#00E599]/25 flex items-center justify-center transition-all group-hover:bg-[#00E599]/20">
            <Brain size={18} className="text-[#00E599]" />
          </div>
          <span className="font-black text-lg tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            CodeIntel<span className="text-[#00E599]"> AI</span> Labs
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              data-testid={`navbar-link-${link.label.toLowerCase()}`}
              className="text-sm font-medium text-[#A1A1AA] hover:text-white transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+919958891551"
            data-testid="navbar-book-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-black bg-[#00E599] rounded-full px-5 py-2.5 hover:bg-[#00ff99] transition-all duration-200 hover:shadow-[0_0_20px_rgba(0,229,153,0.4)]"
          >
            <Phone size={13} />
            Book a Call
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="navbar-mobile-menu-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden bg-[#050505]/98 backdrop-blur-xl border-b border-white/6 px-6 py-6"
        >
          <div className="flex flex-col gap-5">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm font-medium text-[#A1A1AA] hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="tel:+919958891551"
              className="inline-flex items-center gap-2 text-sm font-semibold text-black bg-[#00E599] rounded-full px-5 py-3 w-fit mt-2"
            >
              <Phone size={13} />
              Book a Call
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
